/**
 * Contextual Help FAQ Configuration
 *
 * Collapsible FAQ items shown at the bottom of every HelpPopup.
 * Keyed by the same featureKey as HELP_CONTENT (src/config/helpContent.js).
 *
 * Each entry is an array of { q, a } — plain Hebrew strings.
 * Modal-level keys reuse their tab-level FAQ via aliasing at the bottom.
 *
 * Optional `showIf` field filters items by audience (see HelpPopup):
 *   "multiTherapist" — hidden in solo clinics (clinic.isSoloClinic)
 *   "soloClinic"     — shown only in solo clinics
 *   "admin"          — shown only to clinic owner/admin
 *   "therapist"      — hidden from owner/admin
 * Omit for items relevant to everyone.
 */

export const HELP_FAQ = {
  // ============================================
  // PAGE-LEVEL
  // ============================================

  home_dashboard: [
    {
      q: "האם הנתונים בדף הבית מתעדכנים אוטומטית?",
      a: "כן. כל שינוי במערכת — פגישה חדשה ביומן, משימה שסומנה, תשלום שתועד — משתקף מיד בסעיפים ובסטטיסטיקות, בלי צורך לרענן.",
    },
    {
      q: "למה פגישה מופיעה ב\"פעולות ממתינות\"?",
      a: "כל פגישה שהסתיימה עוברת לשם אוטומטית, וממתינה שתאשרו שהתקיימה, תתעדו סיכום ותטפלו בתשלום. עד שלא טופלה — היא נשארת ברשימה, כדי ששום דבר לא יפול בין הכיסאות.",
    },
    {
      q: "אני רואה כאן את כל הקליניקה או רק את עצמי?",
      a: "דף הבית מציג רק את הנתונים האישיים שלך. מנהל קליניקה שרוצה מבט-על על כל המטפלים ימצא אותו בעמוד \"תמונת מצב\" באזור הניהול.",
      showIf: "multiTherapist",
    },
  ],

  pending_actions: [
    {
      q: "מתי פגישה נכנסת לרשימה?",
      a: "אוטומטית, ברגע ששעת הסיום שלה עברה. לא צריך לעשות שום דבר — המערכת עוקבת אחרי היומן ומעבירה כל פגישה שהסתיימה לכאן.",
    },
    {
      q: "מה קורה אם דילגתי על שלב התשלום?",
      a: "החוב נשאר פתוח ומופיע בעמוד הפיננסים ובלשונית התשלומים בכרטיס המטופל — שום דבר לא הולך לאיבוד, ואפשר לגבות ולתעד בכל שלב מאוחר יותר.",
    },
    {
      q: "איפה מוצאים סיכום שכתבתי דרך התהליך?",
      a: "בלשונית \"טיפולים\" בכרטיס המטופל — יחד עם כל היסטוריית התיעודים שלו.",
    },
  ],

  patients_list: [
    {
      q: "האם מטפלים אחרים רואים את המטופלים שלי?",
      a: "לא. כל מטפל רואה רק את המטופלים שלו. מנהל הקליניקה יכול לצפות בכל המטופלים דרך עמוד \"ניהול מטופלים\" — במצב צפייה בלבד.",
      showIf: "multiTherapist",
    },
    {
      q: "אפשר למחוק מטופל?",
      a: "לא ניתן למחוק לצמיתות — רשומות מטופלים נשמרות מטעמי רגולציה רפואית. במקום זה משנים סטטוס (למשל \"סיים טיפול\") והמטופל יורד מהתצוגה הפעילה.",
    },
    {
      q: "פנה אליי לקוח חדש — להוסיף אותו כאן?",
      a: "אם עדיין לא התחיל טיפול, עדיף לפתוח אותו כ\"מתעניין\" בארגז הכלים. כשיתקדם — ממירים אותו למטופל בלחיצה, והפרטים והיסטוריית הפנייה עוברים איתו.",
    },
  ],

  patient_profile: [
    {
      q: "מי עוד רואה את המידע בכרטיס?",
      a: "רק את/ה כמטפל/ת של המטופל. מנהל הקליניקה יכול לצפות בכרטיס במצב קריאה בלבד דרך עמוד ניהול המטופלים.",
      showIf: "multiTherapist",
    },
    {
      q: "למה אי אפשר לערוך תיעוד טיפול ישן?",
      a: "תיעודים ננעלים אחרי שמירה — דרישה רגולטורית לרשומה רפואית שלא ניתנת לשינוי בדיעבד. תמיד אפשר להוסיף הערה חדשה לתיעוד קיים.",
    },
    {
      q: "איך הרקע הקליני משפיע על סיכומי ה-AI?",
      a: "ה-AI נעזר ברקע הקליני שמילאת (אבחנות, מטרות, רגישויות) כדי לנסח סיכומים מדויקים ורלוונטיים. כרטיס מלא = סיכומים טובים יותר.",
    },
  ],

  create_patient: [
    {
      q: "אפשר להשלים פרטים אחר כך?",
      a: "כן. רק שם משפחה וטלפון הם חובה — כל השאר ניתן להשלמה בכל רגע דרך לשונית \"פרופיל\" בכרטיס המטופל.",
    },
    {
      q: "מה עושה שדה התעריף האישי?",
      a: "תעריף שמוגדר למטופל גובר על תעריף סוג הטיפול ועל תעריף ברירת המחדל של הקליניקה — הוא ישמש לחישוב אוטומטי של הסכום בכל פגישה שלו.",
    },
    {
      q: "המטופל הגיע דרך פנייה (מתעניין)?",
      a: "עדיף להמיר את המתעניין למטופל מלשונית המתעניינים בארגז הכלים — כך הפרטים עוברים אוטומטית וההיסטוריה של הפנייה נשמרת.",
    },
  ],

  calendar: [
    {
      q: "מה קורה כשפגישה מסתיימת?",
      a: "המערכת מעבירה אותה אוטומטית ל\"פעולות ממתינות\" בדף הבית — שם מאשרים שהתקיימה, מתעדים סיכום ומטפלים בתשלום.",
    },
    {
      q: "אפשר לסנכרן עם Google Calendar?",
      a: "כן — מהגדרות היומן (כפתור גלגל השיניים). הפגישות מ-CliniKit יופיעו ביומן הגוגל שלך.",
    },
    {
      q: "איך משנים את משך הפגישה שנקבע אוטומטית?",
      a: "בהגדרות היומן מגדירים \"משך ברירת מחדל\" (למשל 50 דקות). בכל פגישה ספציפית אפשר כמובן לשנות ידנית.",
    },
  ],

  finance: [
    {
      q: "מאיפה מגיע הסכום שמופיע על כל פגישה?",
      a: "לפי סדר עדיפות: תעריף אישי של המטופל ← תעריף סוג הטיפול ← תעריף ברירת המחדל של הקליניקה. אפשר תמיד לערוך ידנית.",
    },
    {
      q: "מה ההבדל בין תשלום \"צפוי\" ל\"ממתין\"?",
      a: "\"צפוי\" — פגישה עתידית שטרם התקיימה. \"ממתין\" — פגישה שהסתיימה והתשלום עליה עדיין לא נגבה, כלומר חוב פתוח.",
    },
    {
      q: "איך מפיקים חשבונית מכאן?",
      a: "CliniKit אינה מנפיקה חשבוניות בעצמה — מחברים ספק חשבוניות חיצוני שהמערכת יודעת לעבוד איתו, כמו מורנינג (Green Invoice), דרך הגדרות הקליניקה. אחרי החיבור, המערכת מפיקה את החשבונית דרך הספק בלחיצה — מכאן, מהפגישה או מדף הבית.",
    },
  ],

  finance_reports: [
    {
      q: "למה ההכנסה כאן שונה ממה שאני רואה בעמוד הפיננסים?",
      a: "הדוחות מציגים הכנסה שנגבתה בפועל בחודש הנבחר. חובות פתוחים נספרים בצפי — אבל לא בהכנסה בפועל, עד שנגבו.",
    },
    {
      q: "מה בדיוק מייצא כפתור ה-CSV?",
      a: "את תוכן הטבלה המפורטת, כולל הסינון והמיון שבחרת — מוכן להעברה להנהלת חשבונות או לעיבוד באקסל.",
    },
    {
      q: "כל מטפל יכול לראות דוחות?",
      a: "תלוי בהרשאות. מנהל הקליניקה קובע בהגדרות הקליניקה אילו מטפלים מקבלים גישה לפיננסים — בלי הרשאה, העמוד לא מוצג.",
      showIf: "multiTherapist",
    },
  ],

  toolbox: [
    {
      q: "מה ההבדל בין משימה לפתק?",
      a: "משימה היא משהו שצריך לעשות — עם סטטוס, עדיפות ומעקב. פתק הוא משהו שצריך לזכור בתאריך מסוים. שניהם מופיעים גם בדף הבית.",
    },
    {
      q: "מה ההבדל בין טופס לשאלון?",
      a: "טופס הוא מסמך לקריאה וחתימה דיגיטלית (למשל הסכמה מדעת). שאלון אוסף תשובות מובנות — סקאלות ובחירה מרובה — למעקב והערכה. שניהם נשלחים בקישור והתוצאות נשמרות בכרטיס המטופל.",
    },
    {
      q: "המטופל צריך להתקין אפליקציה כדי למלא?",
      a: "לא. הקישור נפתח בדפדפן של הטלפון — ממלאים, חותמים, וזהו. התוצאה מגיעה ישירות אליך.",
    },
  ],

  clinic_files: [
    {
      q: "מי רואה את הקבצים כאן?",
      a: "כל המטפלים בקליניקה — זה המאגר המשותף. קבצים של מטופל ספציפי שייכים ל\"תיק מטופל\" בכרטיס שלו, ואותם רואה רק המטפל שלו.",
      showIf: "multiTherapist",
    },
    {
      q: "למה אני לא מצליח/ה למחוק קובץ?",
      a: "רק מי שהעלה את הקובץ — או מנהל הקליניקה — יכול לערוך או למחוק אותו. כולם יכולים לצפות, להוריד ולהדפיס.",
      showIf: "multiTherapist",
    },
    {
      q: "מה ההבדל בין התיקייה שלי לקבצים בכרטיס מטופל?",
      a: "כאן שומרים חומרים כלליים — תבניות, נהלים וחומרי הדרכה. קבצים ששייכים למטופל ספציפי שמורים בתיק שלו, בכרטיס המטופל.",
      showIf: "soloClinic",
    },
    {
      q: "אילו קבצים ובאיזה גודל אפשר להעלות?",
      a: "PDF, תמונות, Word, Excel ועוד — עד 15MB לקובץ. הקבצים נספרים במכסת האחסון הכוללת של הקליניקה.",
    },
  ],

  forms: [
    {
      q: "המטופל חתם — איפה רואים את הטופס?",
      a: "בלשונית \"טפסים ושאלונים\" בכרטיס המטופל, ובנוסף מתקבלת התראה במערכת ברגע החתימה.",
    },
    {
      q: "אפשר לשלוח את אותו טופס לכמה מטופלים?",
      a: "כן — יוצרים את הטופס פעם אחת, וכל שליחה מייצרת קישור ייחודי למטופל. בלשונית \"ממתינים\" עוקבים מי חתם ומי לא.",
    },
    {
      q: "החתימה הדיגיטלית קבילה?",
      a: "כן. התהליך כולל אימות זהות של המטופל לפני החתימה, והמסמך החתום נשמר בתיק בהתאם לדרישות התיעוד של משרד הבריאות.",
    },
  ],

  treatment_doc: [
    {
      q: "למה התיעוד ננעל אחרי שמירה?",
      a: "דרישה רגולטורית — רשומה רפואית לא ניתנת לשינוי בדיעבד. אם צריך לתקן או להשלים, מוסיפים הערה חדשה לתיעוד.",
    },
    {
      q: "מה בדיוק עושה כפתור ה-AI?",
      a: "מנסח סיכום מקצועי מתוך מה שכתבת, בשילוב הרקע הקליני מכרטיס המטופל. הסיכום מוצג לך לעריכה ואישור — שום דבר לא נשמר בלי בדיקה שלך.",
    },
    {
      q: "איפה מוצאים את התיעוד אחר כך?",
      a: "בלשונית \"טיפולים\" בכרטיס המטופל. קבצים שצירפת לתיעוד יופיעו גם בתיק המטופל (לשונית הקבצים).",
    },
  ],

  live_session: [
    {
      q: "מה קורה אם הדפדפן נסגר באמצע?",
      a: "ההערות נשמרות באופן שוטף — אפשר לחזור למפגש ולהמשיך בדיוק מאיפה שהפסקת.",
    },
    {
      q: "ההערות נשמרות בכרטיס המטופל?",
      a: "הסיכום הסופי — כן. בסיום המפגש עוברים ליצירת תיעוד טיפול עם כל ההערות מיובאות, ומשם התיעוד נשמר בלשונית \"טיפולים\".",
    },
    {
      q: "המטופל רואה מה שאני רושם/ת?",
      a: "לא. המפגש החי הוא כלי עבודה פנימי שלך בלבד — המסך והתוכן לא משותפים עם אף אחד.",
    },
  ],

  settings: [
    {
      q: "שיניתי תעריף — זה משפיע על פגישות קיימות?",
      a: "לא. התעריף החדש חל רק על פגישות חדשות. פגישות שכבר נקבעו שומרות את הסכום שנקבע בהן.",
    },
    {
      q: "מה גובר — התעריף שלי או תעריף שהוגדר למטופל?",
      a: "תעריף אישי של מטופל גובר תמיד. הסדר המלא: תעריף מטופל ← תעריף סוג הטיפול ← תעריף ברירת המחדל.",
    },
    {
      q: "איפה מגדירים שעות עבודה וימי חופש?",
      a: "בהגדרות היומן — כפתור גלגל השיניים בעמוד היומן. כאן מנהלים פרופיל, התראות ותגים בלבד.",
    },
  ],

  support_center: [
    {
      q: "איך אדע שהתקבלה תשובה לפנייה?",
      a: "תקבל/י התראה במערכת, והתשובה תופיע בתוך הפנייה עצמה — כמו שיחת צ'אט שאפשר להמשיך.",
    },
    {
      q: "אפשר לפנות לתמיכה בלי להגיע לעמוד הזה?",
      a: "כן — בכל חלון עזרה (כפתור ה-\"?\") יש \"שאל שאלה\" שפותח פנייה ישירות מהמקום שבו אתם נמצאים, עם ההקשר כבר מצורף.",
    },
    {
      q: "על מה אפשר לפנות?",
      a: "הכל — תקלות, שאלות הדרכה, בקשות לפיצ'רים ורעיונות לשיפור. ככל שהתיאור מפורט יותר (מה עשית, מה ציפית, מה קרה), נענה מהר יותר.",
    },
  ],

  // ============================================
  // MODAL-LEVEL
  // ============================================

  appointment_modal: [
    {
      q: "מה קורה אחרי שהפגישה מסתיימת?",
      a: "המערכת מעבירה אותה אוטומטית ל\"פעולות ממתינות\" בדף הבית — לאישור קיום, תיעוד סיכום וגביית תשלום.",
    },
    {
      q: "איך נקבע הסכום לתשלום?",
      a: "אוטומטית: תעריף אישי של המטופל, ואם אין — תעריף סוג הטיפול או תעריף ברירת המחדל של הקליניקה. תמיד אפשר לשנות ידנית.",
    },
    {
      q: "עריכת מפגש אחד בסדרה משנה את כולם?",
      a: "לא — כל מפגש בסדרה עצמאי. אפשר לערוך, להזיז או למחוק מפגש בודד בלי להשפיע על השאר.",
    },
  ],

  calendar_settings: [
    {
      q: "איך מגדירים חופשה או היעדרות?",
      a: "בלשונית 'היעדרויות' — בוחרים סוג (חופשה/מחלה/אחר), תאריכים, כל היום או שעות, והערה. הצוות יראה 'לא זמין/ה' ביומן ומנהלי הקליניקה יקבלו עדכון. תורים קיימים לא מבוטלים — המערכת רק מזהירה אם יש כאלה.",
    },
    {
      q: "חסימת יום מוחקת פגישות שכבר נקבעו בו?",
      a: "לא — פגישות קיימות נשארות במקומן. החסימה מונעת קביעת פגישות חדשות באותו יום.",
    },
    {
      q: "ההגדרות כאן משפיעות על שאר המטפלים?",
      a: "לא — אלה הגדרות אישיות. לכל מטפל בקליניקה יומן, שעות עבודה והגדרות משלו.",
      showIf: "multiTherapist",
    },
    {
      q: "מה נותן סנכרון Google Calendar?",
      a: "הפגישות מ-CliniKit מופיעות אוטומטית ביומן הגוגל שלך — רואים הכל במקום אחד גם מחוץ למערכת.",
    },
  ],

  income_modal: [
    {
      q: "מה זה פיצול תשלום?",
      a: "כשמטופל משלם רק חלק מהחוב — הסכום ששולם נרשם, והיתרה נשארת כחוב פתוח במעקב בעמוד הפיננסים ובכרטיס המטופל.",
    },
    {
      q: "חובה לקשר את התשלום לפגישה?",
      a: "לא חובה, אבל מומלץ — הקישור סוגר את החוב של אותה פגישה ושומר את המעקב הפיננסי בכרטיס המטופל מדויק.",
    },
    {
      q: "תיעדתי תשלום בטעות — מה עושים?",
      a: "מהיסטוריית התשלומים בעמוד הפיננסים אפשר לערוך או לבטל את התשלום. אם כבר הופקה חשבונית — מפיקים חשבונית זיכוי במורנינג.",
    },
  ],

  invoice_modal: [
    {
      q: "איפה נשמרת החשבונית?",
      a: "במורנינג ובמערכת — מקושרת לתשלום או לפגישה, וזמינה לצפייה והורדה מכרטיס המטופל ומעמוד הפיננסים.",
    },
    {
      q: "מה ההבדל בין חשבונית מס/קבלה לקבלה?",
      a: "חשבונית מס/קבלה היא מסמך מס מלא (עוסק מורשה / חברה). קבלה היא אישור תשלום בלבד — המתאים לעוסק פטור. בספק — התייעצו עם רואה החשבון.",
    },
    {
      q: "טעיתי בחשבונית — אפשר למחוק?",
      a: "מסמכי מס לא נמחקים. מפיקים חשבונית זיכוי שמבטלת את המקורית, ואז מפיקים חשבונית חדשה ונכונה.",
    },
    {
      q: "למה קיבלתי קבלה או חשבונית מס ולא חשבונית מס/קבלה?",
      a: "סוג המסמך נקבע לפי ברירת המחדל של הקליניקה. משנים אותה בהגדרות קליניקה ← אינטגרציות ← מורנינג ← הגדרות הפקה, בלי להזין מפתח מחדש. עוסק פטור יכול להפיק רק קבלה.",
    },
  ],

  // ============================================
  // TOOLBOX TABS
  // ============================================

  tasks_tab: [
    {
      q: "מי רואה את המשימות שלי?",
      a: "רק את/ה. מנהל הקליניקה יכול לצפות במשימות של כל הצוות דרך עמוד הניהול \"משימות ותזכורות\".",
      showIf: "multiTherapist",
    },
    {
      q: "משימה שסומנה \"הסתיים\" נעלמת?",
      a: "היא יורדת מהתצוגה הפעילה ומדף הבית, אבל לא נמחקת — סינון לפי סטטוס \"הסתיים\" יציג אותה.",
    },
    {
      q: "משימה או פתק — מה לבחור?",
      a: "צריך לבצע ולעקוב אחרי התקדמות? משימה. רק להיזכר במשהו בתאריך מסוים? פתק.",
    },
  ],

  reminders_tab: [
    {
      q: "איך אדע שהפתק \"הגיע\"?",
      a: "הוא יופיע בדף הבית בסעיף התזכורות החל מהתאריך שהגדרת — בכל כניסה למערכת תראו אותו עד שיטופל.",
    },
    {
      q: "מה עושים עם פתק שטופל?",
      a: "מסמנים שטופל — והוא יורד מדף הבית ומהרשימה הפעילה.",
    },
    {
      q: "משימה או פתק — מה לבחור?",
      a: "צריך לבצע ולעקוב אחרי סטטוס? משימה. רק להיזכר במשהו בתאריך מסוים — כמו מעקב אחרי מטופל? פתק.",
    },
  ],

  leads_tab: [
    {
      q: "מה קורה כשממירים מתעניין למטופל?",
      a: "נפתח כרטיס מטופל חדש עם הפרטים שכבר מולאו, והמתעניין מסומן כ\"הפך למטופל\" — ההיסטוריה של הפנייה נשמרת.",
    },
    {
      q: "אפשר לשנות את רשימת הסטטוסים?",
      a: "כן — סטטוסי המתעניינים מותאמים אישית. עורכים ומוסיפים בהגדרות המטפל, תחת \"תגים\".",
    },
    {
      q: "למה לתעד ביומן ההתפתחות?",
      a: "כל שיחה וסיכום נשמרים כרונולוגית — כשהמתעניין חוזר אחרי חודש, כל ההקשר מולך ולא מתחילים מאפס.",
    },
  ],

  forms_tab: [
    {
      q: "איך שולחים טופס למטופל?",
      a: "בוחרים טופס ומטופל — נוצר קישור ייחודי לשליחה ב-SMS או בהעתקה. המטופל פותח בטלפון, קורא וחותם.",
    },
    {
      q: "המטופל לא חתם — מה עושים?",
      a: "בלשונית \"ממתינים\" רואים את כל מי שטרם חתם, ואפשר לשלוח תזכורת או קישור מחדש.",
    },
    {
      q: "איפה נשמר הטופס החתום?",
      a: "בכרטיס המטופל, בלשונית \"טפסים ושאלונים\" — עם תאריך החתימה ואימות הזהות. מתקבלת גם התראה ברגע החתימה.",
    },
  ],

  questionnaires_tab: [
    {
      q: "איך עוקבים אחרי התקדמות לאורך זמן?",
      a: "שולחים את אותו שאלון במרווחים קבועים — כל התשובות נשמרות בכרטיס המטופל, ואפשר להשוות בין מועדים ולראות מגמה.",
    },
    {
      q: "המטופל רואה את התשובות הקודמות שלו?",
      a: "לא — כל מילוי עומד בפני עצמו. רק את/ה רואה את ההיסטוריה המלאה בכרטיס.",
    },
    {
      q: "מה ההבדל בין שאלון לטופס?",
      a: "שאלון אוסף תשובות מובנות (סקאלה, בחירה מרובה, טקסט) למעקב והערכה. טופס הוא מסמך לקריאה וחתימה דיגיטלית.",
    },
  ],

  templates_tab: [
    {
      q: "עריכת תבנית משנה תיעודים שכבר נכתבו?",
      a: "לא — העריכה משפיעה רק על שימושים עתידיים. תיעודים קיימים נעולים ולא משתנים.",
    },
    {
      q: "איך התבנית עוזרת ל-AI?",
      a: "ה-AI מזהה את מבנה התבנית ומייצר סיכום שממלא את הסעיפים שלה — התוצאה אחידה ומסודרת בכל התיעודים.",
    },
    {
      q: "כמה תבניות כדאי ליצור?",
      a: "אחת לכל סוג מפגש קבוע — למשל מפגש רגיל, אבחון ראשוני וסיכום תקופה. שלוש-ארבע תבניות מכסות את רוב העבודה.",
    },
  ],

  patient_files: [
    {
      q: "מי רואה את הקבצים בתיק?",
      a: "רק המטפל/ת של המטופל. קבצים שכל הצוות צריך — שומרים ב\"התיקייה שלי\" בארגז הכלים.",
      showIf: "multiTherapist",
    },
    {
      q: "אפשר למחוק קבצים?",
      a: "קבצים שהעלית לתיק — כן. שימו לב: קובץ שמצורף לתיעוד טיפול הוא חלק מהרשומה הרפואית.",
    },
    {
      q: "טפסים חתומים מגיעים לכאן?",
      a: "טפסים ושאלונים נשמרים בלשונית \"טפסים ושאלונים\" בכרטיס. כאן נמצאים קבצים שהועלו ידנית וקבצים שצורפו לתיעודי טיפול.",
    },
  ],

  morning_setup: [
    {
      q: "ה-Token מאובטח?",
      a: "כן — הוא נשמר בצורה מאובטחת בצד השרת ומשמש רק להפקת מסמכים. אנחנו לא שומרים סיסמאות ולא ניגשים לחשבון מעבר לכך.",
    },
    {
      q: "אפשר להתנתק ממורנינג?",
      a: "כן, בכל רגע מהגדרות הקליניקה. מסמכים שכבר הופקו נשארים שמורים וזמינים בחשבון המורנינג שלך.",
    },
    {
      q: "החיבור משפיע על כל המטפלים?",
      a: "כן — החיבור הוא ברמת הקליניקה. כל מטפל שקיבל הרשאת הפקת חשבוניות (בהגדרות הקליניקה) יוכל להפיק דרכו.",
      showIf: "multiTherapist",
    },
  ],

  team: [
    {
      q: "איך מוסיפים איש/אשת צוות?",
      a: "לוחצים \"הוספת איש/אשת צוות\", בוחרים תפקיד (מנהל/ת, מטפל/ת או מזכירות), ממלאים שם, אימייל וטלפון. הכניסה למערכת היא עם הטלפון וקוד SMS — ללא הזמנה.",
    },
    {
      q: "מה ההבדל בין התפקידים?",
      a: "בעל/ת הקליניקה ומנהל/ת — גישה מלאה (רק הבעלים ממנה מנהלים). מטפל/ת — מטופלים, יומן ודוחות אישיים + הרשאות לפי בחירה. מזכירות — תיאום תורים וקבלה עבור הצוות, ללא יומן ומטופלים אישיים.",
    },
    {
      q: "מה זה \"הרשאות מותאמות\"?",
      a: "לכל תפקיד יש ברירת מחדל. כשמשנים הרשאה בודדת למטפל/ת מסוים/ת היא מסומנת \"אישי\" וגוברת על ברירת המחדל של הקליניקה. \"איפוס לברירות המחדל של התפקיד\" מחזיר את הכל.",
    },
    {
      q: "מה קורה כשמבטלים \"גישה למידע רפואי רגיש\"?",
      a: "המטפל/ת לא רואה הערות קליניות ולא פותח/ת דוחות טיפול — גם של המטופלים שלו/ה. דוחות קיימים נשמרים. מתאים למתמחים/ות ועוזרים/ות שעובדים/ות על תיאום בלבד.",
    },
    {
      q: "השינוי בהרשאות דורש התחברות מחדש?",
      a: "לא. ההרשאות מתעדכנות תוך שניות אצל המטפל/ת, ומתקבלת התראה \"ההרשאות שלך בקליניקה עודכנו\".",
    },
    {
      q: "איך מעבירים מטופלים בין מטפלים?",
      a: "בכרטיס איש/אשת הצוות → לשונית מטופלים — מסמנים מטופלים (או את כולם) ולוחצים העברה. אפשר לבחור אם להעביר גם תורים עתידיים; תורים שהסתיימו לעולם לא זזים.",
    },
  ],

  clinic_settings: [
    {
      q: "מה ההבדל בין 'מטופל משויך למטפל/ת' ל'כל המטופלים משותפים'?",
      a: "במצב משויך — כל מטפל/ת רואה רק את המטופלים שלו/ה (אפשר לשתף נקודתית עם 'שיוך מטופל לכמה מטפלים'). במצב משותף — כל הצוות רואה ועורך את כל המטופלים והקבצים; דוחות טיפול משותפים רק אם 'כולל דוחות טיפול' פעיל. כל צפייה בדוח של מטפל/ת אחר/ת נרשמת ביומן.",
      showIf: "multiTherapist",
    },
    {
      q: "מה עושות 'חסימת כפל תורים' ו'חסימת תורים בזמן היעדרות'?",
      a: "כבוי — אשף הפגישות רק מזהיר, וכל אחד/ת יכול/ה לאשר בכל זאת. פעיל — הקביעה נחסמת ורק מנהל/ת יכול/ה לעקוף. כל עקיפה נרשמת ביומן הפעילות.",
      showIf: "multiTherapist",
    },
    {
      q: "איפה מנהלים את הצוות?",
      a: "בעמוד \"צוות הקליניקה\" (באזור ניהול הקליניקה בתפריט, או דרך הכפתור כאן) — הוספה, תפקידים, הרשאות אישיות, העברת מטופלים והשבתה.",
      showIf: "multiTherapist",
    },
    {
      q: "על מה שולטות ברירות המחדל לכל המטפלים?",
      a: "גישה לפיננסים, הפקת חשבוניות וייצוא נתונים לכל מי שאין לו/לה הרשאה אישית. הרשאה אישית בעמוד הצוות גוברת על ברירת המחדל. מנהלי הקליניקה תמיד רואים הכל.",
      showIf: "multiTherapist",
    },
    {
      q: "איפה מגדירים סוגי טיפול ותעריפים?",
      a: "בלשונית ההתאמה האישית — לכל סוג טיפול אפשר לקבוע תעריף, והוא ישמש לחישוב אוטומטי של סכום התשלום בפגישות (אלא אם למטופל הוגדר תעריף אישי).",
    },
    {
      q: "מה ההבדל מ\"הגדרות מטפל\"?",
      a: "כאן — הגדרות שמשפיעות על כל הקליניקה: חדרים, סוגי טיפול, אינטגרציות והרשאות. שם — העדפות אישיות של כל מטפל: פרופיל, תעריף והתראות.",
    },
  ],

  // ============================================
  // ADMIN PAGES
  // ============================================

  admin_snapshot: [
    {
      q: "הנתונים כאן בזמן אמת?",
      a: "כן — העמוד משקף את המצב הנוכחי של הקליניקה ומתעדכן אוטומטית עם כל שינוי.",
    },
    {
      q: "מטפלים רגילים רואים את העמוד?",
      a: "לא — רק מנהל הקליניקה. כל מטפל רואה את הנתונים האישיים שלו בדף הבית שלו.",
    },
    {
      q: "מספר בגבייה נראה לא הגיוני — מה לבדוק?",
      a: "לרוב הסיבה היא פגישות שטרם טופלו ב\"פעולות ממתינות\" של המטפלים — תשלומים שלא תועדו נספרים כחובות פתוחים.",
    },
  ],

  admin_patients: [
    {
      q: "אפשר לערוך מטופל של מטפל אחר?",
      a: "לא — הצפייה כאן לקריאה בלבד, לשמירה על אחריות טיפולית ופרטיות. עריכה נעשית רק על ידי המטפל של המטופל.",
    },
    {
      q: "החיפוש כאן שונה מעמוד \"המטופלים שלי\"?",
      a: "כן — כאן החיפוש סורק את כל מטופלי הקליניקה, אצל כל המטפלים. בעמוד הרגיל כל מטפל מחפש רק בין המטופלים שלו.",
    },
    {
      q: "איך בודקים חלוקת עומס בין מטפלים?",
      a: "מסננים לפי מטפל ומשווים כמות מטופלים פעילים. עמוד \"תמונת מצב\" משלים את התמונה עם נתוני פגישות וניצול.",
    },
  ],

  admin_calendar: [
    {
      q: "מה זה 'זמינות הצוות'?",
      a: "טבלה שבועית של שעות העבודה, החסימות וההיעדרויות של כל המטפלים, עם ניצולת (תורים מול מקומות פנויים). לחיצה על תא פותחת את היומן של המטפל/ת באותו יום.",
    },
    {
      q: "איפה רואים מי צפה בדוחות טיפול של אחרים?",
      a: "ניהול הקליניקה → יומן פעילות → לשונית 'צפיות בדוחות טיפול'. נרשמת כל פתיחה של דוח שנכתב על ידי מטפל/ת אחר/ת, כולל על ידי מנהלים. המחבר/ת לא מקבל/ת התראה.",
    },
    {
      q: "אפשר לקבוע פגישה למטפל אחר מכאן?",
      a: "התצוגה כאן לצפייה. לחיצה על מטפל פותחת את היומן המלא שלו — ושם מנהלים את הפגישות.",
    },
    {
      q: "איך מוסיפים חדרים לתצוגה?",
      a: "מגדירים חדרי טיפול בהגדרות הקליניקה, בלשונית ניהול החדרים — ותצוגת התפוסה תופיע כאן אוטומטית.",
    },
    {
      q: "מאיפה מגיעות שעות העבודה של כל מטפל?",
      a: "כל מטפל מגדיר לעצמו שעות עבודה בהגדרות היומן האישיות שלו — כאן רואים את התמונה המרוכזת של כולם.",
    },
  ],

  admin_tasks: [
    {
      q: "מה ההבדל מעמוד המשימות הרגיל?",
      a: "בארגז הכלים כל מטפל רואה ומנהל רק את שלו. כאן מנהל הקליניקה רואה את כולם — עם סינון לפי מטפל וסטטוס, לצפייה ומעקב.",
    },
    {
      q: "איך מזהים עומס על מטפל?",
      a: "מסננים לפי מטפל — ריבוי משימות פתוחות או באיחור (מסומנות באדום) הוא סימן שכדאי לחלק מחדש או לעזור.",
    },
  ],
};

// Modal-level dialogs reuse their tab-level FAQ — same feature, same questions.
HELP_FAQ.task_modal = HELP_FAQ.tasks_tab;
HELP_FAQ.reminder_modal = HELP_FAQ.reminders_tab;
HELP_FAQ.lead_modal = HELP_FAQ.leads_tab;
HELP_FAQ.form_modal = HELP_FAQ.forms_tab;
HELP_FAQ.template_modal = HELP_FAQ.templates_tab;
HELP_FAQ.questionnaire_modal = HELP_FAQ.questionnaires_tab;
